import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Database, Loader2, MessageSquare, RotateCcw, Send } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/Card";
import type { FrageAntwort, FrageGrundlage, FrageStatus } from "@/lib/types";

/**
 * Fragen an die Daten, als Gespräch unter dem Bericht.
 *
 * Das Gespräch liegt nur hier im Browser (sessionStorage): Leif speichert
 * weder Fragen noch Antworten und bekommt die bisherigen Wechsel bei jeder
 * Frage mit. Unter jeder Antwort steht, worauf sie beruht: die Abfragen,
 * die Leif ausgeführt hat, mit den gezählten Werten direkt aus der
 * Datenbank. Wer der Antwort nicht traut, sieht so die Zahlen selbst.
 */

type Wechsel = { frage: string; antwort?: FrageAntwort; laeuft?: boolean };

const SPEICHER = "leif:fragen";

const BEISPIELE = [
  t("Wann wurde die Lichtschranke in den letzten 7 Tagen besonders häufig ausgelöst?"),
  t("Welche Fehler gab es gestern am häufigsten?"),
];

function laden(): Wechsel[] {
  try {
    const roh = window.sessionStorage.getItem(SPEICHER);
    const liste = roh ? (JSON.parse(roh) as Wechsel[]) : [];
    // Eine Frage, die beim Verlassen der Seite noch lief, kommt nicht mehr.
    return liste.filter((w) => !w.laeuft);
  } catch {
    return [];
  }
}

/** Das Modell setzt gern Markdown-Fettdruck; hier steht Fließtext. */
function ohneMarkdown(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/g, "$1");
}

type Wert = { label: string; titel: string; anzahl: number; auffaellig: boolean };

/** Kleine Balkenreihe für eine Verteilung aus `meldungen_zaehlen`. */
function Balken({ titel, werte, jedes }: { titel: string; werte: Wert[]; jedes: number }) {
  const max = Math.max(1, ...werte.map((w) => w.anzahl));
  if (werte.every((w) => w.anzahl === 0)) return null;
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-[11px] text-muted-foreground">{titel}</span>
      <div className="flex h-16 items-end gap-px">
        {werte.map((w, i) => (
          <div key={i} className="flex h-full min-w-[3px] flex-1 flex-col justify-end" title={`${w.titel}: ${w.anzahl}`}>
            <div
              className={cn("rounded-t-[2px]", w.auffaellig ? "bg-destructive" : "bg-primary/80")}
              style={{ height: w.anzahl > 0 ? `${Math.max(4, (w.anzahl / max) * 100)}%` : 0 }}
            />
          </div>
        ))}
      </div>
      <div className="flex gap-px text-[9px] leading-none text-muted-foreground">
        {werte.map((w, i) => (
          <span key={i} className="min-w-[3px] flex-1 overflow-visible whitespace-nowrap">
            {i % jedes === 0 ? w.label : ""}
          </span>
        ))}
      </div>
    </div>
  );
}

type Zaehlung = {
  je_tag?: { tag: string; anzahl: number; auffaellig: boolean }[];
  je_uhrzeit?: { uhrzeit: string; anzahl: number; auffaellig: boolean }[];
};

/** Die Verteilung der letzten Zählung, als Bild direkt unter der Antwort. */
function Verteilung({ g }: { g: FrageGrundlage }) {
  const d = g.daten as Zaehlung;
  const tage: Wert[] = (d.je_tag ?? []).map((x) => ({
    // „Mo 05.10.2026“ → „05.10.“
    label: (x.tag.split(" ")[1] ?? x.tag).slice(0, 6),
    titel: x.tag,
    anzahl: x.anzahl,
    auffaellig: x.auffaellig,
  }));
  const uhr: Wert[] = (d.je_uhrzeit ?? []).map((x) => ({
    label: x.uhrzeit.slice(0, 2),
    titel: x.uhrzeit,
    anzahl: x.anzahl,
    auffaellig: x.auffaellig,
  }));
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {tage.length > 1 ? (
        <Balken titel={t("Je Tag")} werte={tage} jedes={Math.max(1, Math.ceil(tage.length / 7))} />
      ) : null}
      {uhr.length ? <Balken titel={t("Je Uhrzeit, über den ganzen Zeitraum")} werte={uhr} jedes={3} /> : null}
    </div>
  );
}

function AntwortBlock({ a }: { a: FrageAntwort }) {
  const zaehlungen = a.grundlage.filter((g) => g.werkzeug === "meldungen_zaehlen" && !("fehler" in g.daten));
  const letzte = zaehlungen[zaehlungen.length - 1];
  return (
    <div className="flex flex-col gap-3">
      {a.fehler ? (
        <div className="flex gap-2 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{a.fehler}</p>
        </div>
      ) : (
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{ohneMarkdown(a.antwort)}</p>
      )}
      {a.ungedeckt?.length ? (
        <div className="flex gap-2 rounded-md bg-warning/10 px-3 py-2 text-xs text-warning">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p>
            {t("Diese Zahlen stehen in keinem Ergebnis aus der Datenbank: {zahlen}. Vor Gebrauch nachprüfen.", {
              zahlen: a.ungedeckt.join(", "),
            })}
          </p>
        </div>
      ) : null}
      {letzte ? <Verteilung g={letzte} /> : null}
      {a.grundlage.length ? (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer select-none">
            <Database className="mr-1 inline h-3.5 w-3.5 align-[-2px]" />
            {t("Grundlage: {n} Abfragen in der Datenbank", { n: a.grundlage.length })}
          </summary>
          <ul className="mt-2 flex flex-col gap-1 pl-5">
            {a.grundlage.map((g, i) => (
              <li key={i} className="list-disc">
                {g.kurz}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

export function Fragen({ berichtId, isAdmin }: { berichtId?: string; isAdmin: boolean }) {
  const [status, setStatus] = useState<FrageStatus | null>(null);
  const [wechsel, setWechsel] = useState<Wechsel[]>(laden);
  const [eingabe, setEingabe] = useState("");
  const ende = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api
      .get<FrageStatus>("/api/bericht/frage")
      .then(setStatus)
      .catch(() => setStatus({ aktiv: false, lizenz: false }));
  }, []);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(SPEICHER, JSON.stringify(wechsel));
    } catch {
      // Voller oder gesperrter Speicher: Das Gespräch geht dann beim
      // Neuladen verloren, mehr nicht.
    }
    ende.current?.scrollIntoView({ block: "nearest" });
  }, [wechsel]);

  const laeuft = wechsel.some((w) => w.laeuft);

  const fragen = async (frage: string) => {
    frage = frage.trim();
    if (!frage || laeuft) return;
    // Mit geht nur, was beantwortet wurde. Absagen sind für das Modell
    // kein Gesprächsstoff.
    const verlauf = wechsel
      .filter((w) => w.antwort && !w.antwort.fehler && w.antwort.antwort)
      .map((w) => ({ frage: w.frage, antwort: w.antwort!.antwort }));
    setWechsel((cur) => [...cur, { frage, laeuft: true }]);
    setEingabe("");
    let antwort: FrageAntwort;
    try {
      antwort = await api.post<FrageAntwort>("/api/bericht/frage", {
        frage,
        verlauf,
        bericht_id: berichtId || undefined,
      });
    } catch (e) {
      antwort = {
        antwort: "",
        grundlage: [],
        schritte: 0,
        fehler: e instanceof Error ? e.message : t("Die Frage konnte nicht gestellt werden."),
      };
    }
    setWechsel((cur) => cur.map((w) => (w.laeuft && w.frage === frage ? { frage, antwort } : w)));
  };

  if (status === null) return null;

  const gesperrt = !status.aktiv || !status.lizenz;

  return (
    <Card
      title={t("Fragen an die Daten")}
      description={t("In eigenen Worten fragen. Leif zählt in der Datenbank, das Sprachmodell wählt die Abfrage und formuliert die Antwort.")}
      actions={
        wechsel.length && !laeuft ? (
          <button
            type="button"
            onClick={() => setWechsel([])}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-card px-2 text-xs font-medium hover:bg-muted"
          >
            <RotateCcw className="h-3.5 w-3.5" /> {t("Neues Gespräch")}
          </button>
        ) : null
      }
    >
      {gesperrt ? (
        <div className="flex gap-2 text-sm text-muted-foreground">
          <MessageSquare className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            {!status.lizenz
              ? t("Braucht eine Lizenz: Das Sprachmodell läuft über den Lizenzserver und wird vom KI-Guthaben bezahlt.")
              : isAdmin
                ? t("Ausgeschaltet. Unter Einstellungen, Bericht lässt es sich einschalten.")
                : t("Ausgeschaltet. Ein Administrator kann es unter Einstellungen, Bericht einschalten.")}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {wechsel.length === 0 ? (
            <div className="flex flex-wrap gap-2">
              {BEISPIELE.map((b) => (
                <button
                  key={b}
                  type="button"
                  onClick={() => setEingabe(b)}
                  className="rounded-full border border-border bg-background px-3 py-1 text-left text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {b}
                </button>
              ))}
            </div>
          ) : null}

          {wechsel.map((w, i) => (
            <div key={i} className="flex flex-col gap-2">
              <div className="self-end rounded-lg bg-primary/10 px-3 py-2 text-sm">{w.frage}</div>
              <div className="rounded-lg border border-border bg-background px-3 py-3">
                {w.laeuft ? (
                  <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> {t("Leif sucht in den Daten…")}
                  </span>
                ) : w.antwort ? (
                  <AntwortBlock a={w.antwort} />
                ) : null}
              </div>
            </div>
          ))}
          <div ref={ende} />

          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void fragen(eingabe);
            }}
          >
            <textarea
              value={eingabe}
              onChange={(e) => setEingabe(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void fragen(eingabe);
                }
              }}
              rows={2}
              maxLength={1000}
              placeholder={t("z.B. Wann wurde an ROB12 die Lichtschranke besonders häufig ausgelöst?")}
              className="min-h-[2.5rem] flex-1 resize-y rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
            <button
              type="submit"
              disabled={laeuft || !eingabe.trim()}
              title={t("Fragen")}
              aria-label={t("Fragen")}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </Card>
  );
}
